import {EMPTY_PAD,GamepadSampler,type PadFrame} from "./gamepad-state";

export type GamepadSnapshot=PadFrame&{available:boolean;error:string|null;usingGamepad:boolean};
const EMPTY:GamepadSnapshot={...EMPTY_PAD,available:true,error:null,usingGamepad:false};
/** Shared, reference-counted poller. UI diagnostics never instantiate Stage or
 * AudioContext. Gameplay consumes the latest sample, not React's refresh rate.
 */
class BrowserGamepad{
  private sampler=new GamepadSampler();
  private users=0;private raf=0;private focused=true;private gameplay=false;
  private frame:GamepadSnapshot=EMPTY;
  private snapshot:GamepadSnapshot=EMPTY;
  private listeners=new Set<()=>void>();
  private frames=new Set<(frame:GamepadSnapshot)=>void>();
  private lastPublish=0;private using=false;private events?:AbortController;
  getSnapshot=()=>this.snapshot;
  getServerSnapshot=()=>EMPTY;
  subscribe=(callback:()=>void)=>{this.listeners.add(callback);return()=>{this.listeners.delete(callback);};};
  onFrame(callback:(frame:GamepadSnapshot)=>void){this.frames.add(callback);return()=>{this.frames.delete(callback);};}
  setGameplay(enabled:boolean){this.gameplay=enabled;}
  movement(fallback:Readonly<{x:number;z:number}>){
    return this.gameplay&&this.focused&&this.frame.standard&&this.frame.armed&&this.using?this.frame.move:fallback;
  }
  private publish(){this.snapshot=this.frame;for(const listener of this.listeners)listener();}
  private clear=()=>{
    this.sampler.reset();this.using=false;
    this.frame={...EMPTY,available:typeof navigator.getGamepads==="function"};
    this.publish();
  };
  private background=()=>{this.focused=false;this.clear();};
  private foreground=()=>{this.focused=!document.hidden;this.clear();};
  private visibility=()=>{document.hidden?this.background():this.foreground();};
  private alternate=()=>{if(this.using){this.using=false;this.frame={...this.frame,usingGamepad:false};this.publish();}};
  private poll=(now:number)=>{
    const available=typeof navigator.getGamepads==="function";
    let error:string|null=null,pad:PadFrame=EMPTY_PAD;
    if(available){
      try{pad=this.sampler.sample(Array.from(navigator.getGamepads()),now,this.focused&&!document.hidden);}
      catch{error="blocked";this.sampler.reset();}
    }
    if(pad.activity)this.using=true;
    if(!pad.connected||!pad.standard||!pad.armed)this.using=false;
    const previous=this.frame;
    this.frame={...pad,available,error,usingGamepad:this.using};
    for(const callback of this.frames)callback(this.frame);
    const changed=this.snapshot.axes.length!==pad.axes.length||this.snapshot.buttons.length!==pad.buttons.length||
      pad.axes.some((v,i)=>v!==this.snapshot.axes[i])||pad.buttons.some((v,i)=>v!==this.snapshot.buttons[i]);
    if((changed&&now-this.lastPublish>=100)||previous.id!==pad.id||previous.armed!==pad.armed||
      previous.standard!==pad.standard||
      previous.usingGamepad!==this.using||previous.error!==error||previous.available!==available){
      this.lastPublish=now;this.publish();
    }
    if(this.users)this.raf=requestAnimationFrame(this.poll);
  };
  acquire(){
    if(typeof window==="undefined")return()=>{};
    if(this.users++===0){
      this.focused=document.hasFocus()&&!document.hidden;
      this.events=new AbortController();const {signal}=this.events;
      window.addEventListener("blur",this.background,{signal});
      window.addEventListener("focus",this.foreground,{signal});
      document.addEventListener("visibilitychange",this.visibility,{signal});
      window.addEventListener("gamepaddisconnected",this.clear,{signal});
      window.addEventListener("pointerdown",this.alternate,{signal,capture:true});
      window.addEventListener("keydown",this.alternate,{signal,capture:true});
      this.raf=requestAnimationFrame(this.poll);
    }
    let released=false;
    return()=>{if(released)return;released=true;if(--this.users===0){
      cancelAnimationFrame(this.raf);this.events?.abort();this.events=undefined;
      this.gameplay=false;this.clear();
    }};
  }
}
export const browserGamepad=new BrowserGamepad();
